import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useEntity, useEntityHistory, useReVerify } from '../api/hooks.js';
import { Button, ButtonLink } from '../components/Button.js';
import { BACK_LINK, cx, LINK, MONO } from '../components/classes.js';
import { ChevronLeft } from '../components/Icons.js';
import { ProblemNote, SkeletonRows } from '../components/Notes.js';
import { RailItem, RailSection, SignalLine, TimelineItem } from '../components/Rail.js';
import { SummaryCell } from '../components/SummaryCell.js';
import { Tabs, type TabSpec } from '../components/Tabs.js';
import {
  companyTypeLabel,
  formatDate,
  formatRead,
  humanise,
  KIND_LABELS,
  sourceLabel,
  titleCase,
} from '../lib/format.js';
import { useSearchStore } from '../store/search.js';
import { canIssue, useSessionStore } from '../store/session.js';
import { IssuePanel } from './profile/IssuePanel.js';
import { summaryOf } from './profile/summary.js';
import {
  GroupTab,
  HistoryTab,
  historyLine,
  LicencesTab,
  RegistryTab,
  ScreeningTab,
  signalDetail,
  SignalsTab,
  TaxTab,
  TendersTab,
  type ProfileTab,
} from './profile/tabs.js';

/** Wireframe §5: the destination. Four cells answer the question; two actions only. */
export function Profile() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const q = useSearchStore((s) => s.q);
  const role = useSessionStore((s) => s.role);
  const entity = useEntity(id);
  const reverify = useReVerify();
  const history = useEntityHistory(id, entity.isSuccess);
  const [tab, setTab] = useState<ProfileTab>('registry');
  const [issuing, setIssuing] = useState(false);
  const p = entity.data?.data;
  const summary = useMemo(() => (p ? summaryOf(p) : null), [p]);

  if (entity.isPending) {
    return (
      <main className="flex flex-col gap-7 px-12 pt-9 pb-16">
        <SkeletonRows rows={5} className="rounded-card border border-line bg-surface" />
      </main>
    );
  }
  if (entity.isError || !p || !summary) {
    return (
      <main className="flex flex-col gap-7 px-12 pt-9 pb-16">
        <Link to={q ? `/search?q=${encodeURIComponent(q)}` : '/'} className={BACK_LINK}>
          <ChevronLeft />
          {q ? `Results for “${q}”` : 'Search'}
        </Link>
        <ProblemNote error={entity.error ?? new Error('No entity')} />
      </main>
    );
  }

  const reg = p.registry?.payload;
  const kindLine = [companyTypeLabel(reg?.companyType) ?? KIND_LABELS[p.kind]]
    .filter(Boolean)
    .join(' · ');
  const openSignals = p.signals.filter((s) => !s.resolved_at);
  const screeningClear =
    p.screening.review_pending.length === 0 && p.screening.hard_key_hits.length === 0;
  const tabs: ReadonlyArray<TabSpec<ProfileTab>> = [
    { key: 'registry', label: 'Registry' },
    { key: 'tax', label: 'Tax' },
    { key: 'licences', label: 'Licences', count: p.licences.length || null },
    { key: 'tenders', label: 'Tenders' },
    {
      key: 'screening',
      label: 'Screening',
      count: screeningClear
        ? '✓'
        : p.screening.review_pending.length + p.screening.hard_key_hits.length,
    },
    { key: 'group', label: 'Group', count: p.group.length || null },
    { key: 'history', label: 'History' },
    { key: 'signals', label: 'Signals', count: openSignals.length || null },
  ];
  const events = (history.data?.data ?? []).filter((h) => h.kind === 'event').slice(0, 3);
  const mayIssue = canIssue(role);
  // A fresh record for this subject, filed as a periodic review (never an
  // idempotent replay — see useReVerify).
  const reVerify = () => {
    reverify.mutate(
      { entity_id: p.id },
      {
        onSuccess: (env) =>
          navigate(`/records/${env.data.id}`, { state: { issued: true } }),
      },
    );
  };

  return (
    <main className="flex flex-col gap-7 px-12 pt-9 pb-16">
      <Link to={q ? `/search?q=${encodeURIComponent(q)}` : '/'} className={BACK_LINK}>
        <ChevronLeft />
        {q ? `Results for “${q}”` : 'Search'}
      </Link>
      <div className="flex items-start justify-between gap-8">
        <div className="flex min-w-0 flex-col gap-2.5">
          <div className="text-[14px] text-mute">{kindLine}</div>
          <h1 className="m-0 text-[36px] font-semibold leading-[1.1] tracking-[-0.02em]">
            {titleCase(p.name)}
          </h1>
          <div className={cx(MONO, 'flex gap-[18px] text-[13px] text-mute')}>
            <span className="text-ink">{p.dira_id ?? 'Dira ID pending'}</span>
            {reg?.entityNumber ? <span>PACRA {reg.entityNumber}</span> : null}
            {p.tax.payload?.tpin ? (
              <span>
                TPIN {p.tax.payload.tpin}
                {p.tax.basis === 'owner_tpin' ? ' (owner)' : ''}
              </span>
            ) : null}
          </div>
          {p.last_verification ? (
            <div className="text-[13px] text-mute">
              Last record issued {formatDate(p.last_verification.issued_at)} ·{' '}
              <Link to={`/records/${p.last_verification.id}`} className={LINK}>
                View
              </Link>
              {mayIssue ? (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={reVerify}
                    disabled={reverify.isPending}
                    className={cx(
                      LINK,
                      'cursor-pointer disabled:cursor-default disabled:opacity-60',
                    )}
                  >
                    {reverify.isPending ? 'Issuing…' : 'Re-verify'}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-3">
          <ButtonLink to="/watchlist">Watch</ButtonLink>
          <Button
            variant="primary"
            onClick={() => setIssuing(true)}
            disabled={!mayIssue}
            title={mayIssue ? undefined : 'Your role is read-only (auditor)'}
          >
            Generate verification record
          </Button>
        </div>
      </div>

      {reverify.isError ? <ProblemNote error={reverify.error} /> : null}

      <section
        aria-label="Summary"
        className="grid grid-cols-4 overflow-hidden rounded-card border border-line bg-surface shadow-lift"
      >
        {summary.map((s, i) => (
          <SummaryCell
            key={s.title}
            first={i === 0}
            status={s.status}
            title={s.title}
            fact={s.fact}
            source={s.source}
          />
        ))}
      </section>

      <Tabs tabs={tabs} active={tab} onChange={setTab} label="Profile sections" />

      <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-8">
        <div>
          {tab === 'registry' ? <RegistryTab p={p} /> : null}
          {tab === 'tax' ? <TaxTab p={p} /> : null}
          {tab === 'licences' ? <LicencesTab p={p} /> : null}
          {tab === 'tenders' ? <TendersTab /> : null}
          {tab === 'screening' ? <ScreeningTab p={p} /> : null}
          {tab === 'group' ? <GroupTab p={p} /> : null}
          {tab === 'history' ? <HistoryTab p={p} /> : null}
          {tab === 'signals' ? <SignalsTab p={p} /> : null}
        </div>
        <aside className="flex flex-col gap-7">
          <RailSection label="LICENCES & SCREENING">
            {p.licences.slice(0, 3).map((l) => (
              <RailItem
                key={`${l.source}:${l.key}`}
                status="pass"
                title={`${sourceLabel(l.source)} record`}
                detail={`${l.key} · ${formatRead(l.fetched_at)}`}
                mono
              />
            ))}
            <RailItem
              status={screeningClear ? 'pass' : 'attention'}
              title={
                screeningClear ? 'Sanctions & debarment clear' : 'Screening needs a look'
              }
              detail={`UN · OFAC · UK · EU · World Bank · ${formatDate(p.as_of ?? p.updated_at)}`}
            />
          </RailSection>
          <RailSection label="OPEN SIGNALS">
            {openSignals.length === 0 ? (
              <div className="text-[13px] text-mute">No open signals.</div>
            ) : (
              openSignals.map((s) => (
                <SignalLine
                  key={s.id}
                  severity={s.severity}
                  title={humanise(s.type)}
                  detail={signalDetail(s)}
                />
              ))
            )}
          </RailSection>
          <RailSection label="RECENT CHANGES">
            {history.isPending ? (
              <div className="text-[13px] text-mute">Reading history…</div>
            ) : events.length === 0 ? (
              <div className="text-[13px] text-mute">
                No changes observed since the first read.
              </div>
            ) : (
              <div className="flex flex-col">
                {events.map((h, i) => (
                  <TimelineItem
                    key={h.id}
                    when={formatDate(h.at)}
                    tone={i === 0 ? 'warn' : i === 1 ? 'good' : 'ink'}
                    last={i === events.length - 1}
                  >
                    {historyLine(h)}
                  </TimelineItem>
                ))}
              </div>
            )}
          </RailSection>
        </aside>
      </div>

      <IssuePanel entity={p} open={issuing} onClose={() => setIssuing(false)} />
    </main>
  );
}
